import React, { useEffect, useMemo } from 'react';
import { Button, Modal, Select, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { toTrainingCategorySelectOptions } from '../../helpers/trainingHelpers';
import { TrainingCategory, TrainingMaterial } from '../../types/training';

type Props = {
    opened: boolean;
    onClose: () => void;
    material: TrainingMaterial | null;
    categories: TrainingCategory[];
    loading?: boolean;
    onSave: (values: {
        id: number;
        title: string;
        description: string | null;
        categoryId: number;
    }) => Promise<void>;
};

export function TrainingMaterialEditModal({
    opened,
    onClose,
    material,
    categories,
    loading = false,
    onSave,
}: Props) {
    const form = useForm({
        initialValues: {
            title: '',
            description: '',
            categoryId: '',
        },
        validate: {
            title: value =>
                value.trim().length === 0 ? 'Title is required' : null,
            categoryId: value => (!value ? 'Category is required' : null),
        },
    });

    useEffect(() => {
        if (!opened || !material) {
            return;
        }
        form.setValues({
            title: material.title,
            description: material.description ?? '',
            categoryId: String(material.categoryId),
        });
        form.clearErrors();
    }, [opened, material]);

    const categoryOptions = useMemo(
        () => toTrainingCategorySelectOptions(categories),
        [categories],
    );

    const handleSubmit = form.onSubmit(async values => {
        if (!material) {
            return;
        }
        await onSave({
            id: material.id,
            title: values.title.trim(),
            description: values.description.trim() || null,
            categoryId: Number(values.categoryId),
        });
    });

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Edit material"
            centered
            size="lg"
            keepMounted={false}
            classNames={{
                title: 'font-semibold text-lg',
            }}
        >
            <form onSubmit={handleSubmit} className="p-3">
                <TextInput
                    label="Title"
                    placeholder="Material title"
                    className="mb-3"
                    {...form.getInputProps('title')}
                />
                <Textarea
                    label="Description"
                    placeholder="Optional description"
                    minRows={3}
                    resize="vertical"
                    className="mb-3"
                    {...form.getInputProps('description')}
                />
                <Select
                    label="Category"
                    placeholder="Select a category"
                    data={categoryOptions}
                    searchable
                    className="mb-4"
                    {...form.getInputProps('categoryId')}
                />
                <div className="flex justify-end mt-6 gap-3">
                    <Button
                        fullWidth={false}
                        size="sm"
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth={false}
                        size="sm"
                        type="submit"
                        loading={loading}
                    >
                        Save changes
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
